'use client';

import { ArrowRight } from 'lucide-react';
import Link from 'next/link';
import { MultiFormatImage } from '@/components/ui/MultiFormatImage';
import { useProductContext } from '@/contexts/ProductContext';

/**
 * Normalise a product name for fuzzy matching:
 *  - lowercase
 *  - strip trailing plurals: "Boxes" → "Box", "Shields" → "Shield"
 *  - collapse whitespace
 */
function normalise(name) {
    return name.toLowerCase().replace(/es$/, '').replace(/s$/, '').trim();
}

/**
 * Render a row of related product cards.
 *
 * `relatedNames` is the string array from PRODUCT_CONFIGS (e.g.
 * ["Versa Shields", "Trench Shield Boxes", "Supa Shield"]).
 * We fuzzy-match each entry against the live product list from context.
 */
export default function RelatedProducts({ relatedNames = [] }) {
    const { products } = useProductContext();

    if (!relatedNames.length || !products?.length) return null;

    const matched = relatedNames
        .map((name) => {
            const norm = normalise(name);
            return products.find((p) => {
                if (!p?.name || !p?.href) return false;
                // Match by normalised name or by slug in href
                const pSlug = p.href.split('/').pop();
                return normalise(p.name) === norm || normalise(pSlug) === norm;
            });
        })
        .filter(Boolean)
        .slice(0, 3);

    if (!matched.length) return null;

    return (
        <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Related Products
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {matched.map((p) => (
                    <Link
                        key={p.id || p.href}
                        href={p.href}
                        className="group bg-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col"
                    >
                        <div className="aspect-[4/3] bg-gray-50 flex items-center justify-center p-4">
                            <MultiFormatImage
                                src={
                                    p.img ||
                                    '/placeholder.svg?height=300&width=400&query=product'
                                }
                                alt={p.name}
                                width={400}
                                height={300}
                                className="max-h-full object-contain group-hover:scale-105 transition-transform duration-300"
                                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                            />
                        </div>
                        <div className="p-4 flex items-center justify-between">
                            <span className="font-semibold text-gray-900 group-hover:text-brand transition-colors">
                                {p.name}
                            </span>
                            <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-brand group-hover:translate-x-1 transition-all" />
                        </div>
                    </Link>
                ))}
            </div>
        </section>
    );
}
